import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { normaliseTier, CHART_TYPES } from "@/lib/NseChartEngine";
import ChartControls from "@/components/chart/ChartControls";
import ChartCanvas from "@/components/chart/ChartCanvas";

const TYPE_KEY = "md_chart_type_pref";
const IND_KEY = "md_chart_indicators_pref";
const DEFAULT_INDICATORS = { ma: { enabled: false }, ema: { enabled: false }, bollinger: { enabled: false }, rsi: { enabled: false }, macd: { enabled: false } };

function loadPref(key, fallback) {
    try {
        const raw = localStorage.getItem(key);
        return raw ? { ...fallback, ...JSON.parse(raw) } : fallback;
    } catch (e) {
        return fallback;
    }
}

const VALID_CHART_TYPE_IDS = new Set(Object.values(CHART_TYPES).map((c) => c.id));

/**
 * chartType is a plain string id (e.g. "LINE"), NOT an object — object-
 * spreading it via loadPref() (as the indicators pref does) corrupts it
 * into `{0:'L',1:'I',...}`, which NseChartEngine.renderChart then rejects
 * as an unrecognized chart type. This loader reads it as a string and
 * validates against CHART_TYPES, also self-healing any localStorage value
 * already corrupted by that bug so affected users recover automatically.
 */
function loadChartTypePref() {
    const fallback = CHART_TYPES.LINE.id;
    try {
        const raw = localStorage.getItem(TYPE_KEY);
        if (!raw) return fallback;
        let parsed;
        try { parsed = JSON.parse(raw); } catch (e) { parsed = raw; }
        if (parsed && typeof parsed === "object") {
            // Recover a previously-corrupted {0:'L',1:'I',...} shape.
            parsed = Object.keys(parsed).sort((a, b) => a - b).map((k) => parsed[k]).join("");
        }
        if (VALID_CHART_TYPE_IDS.has(parsed)) {
            // Persist the recovered value so the corrupted object shape
            // doesn't keep needing recovery on every future load.
            localStorage.setItem(TYPE_KEY, JSON.stringify(parsed));
            return parsed;
        }
        localStorage.setItem(TYPE_KEY, JSON.stringify(fallback));
        return fallback;
    } catch (e) {
        return fallback;
    }
}

/**
 * InstrumentChartWidget — universal chart backed by /market/daily/{symbol}
 * (the simulator's own OHLC store), so it renders for EVERY tradable
 * instrument — equities, indices, commodities, FX — unlike the old
 * MansaHistoryChart which only covered symbols with a live-provider match.
 *
 * Chart type + indicator toggles are remembered across symbols/sessions
 * (localStorage), matching the "Admin Tools" sidebar's persistence pattern.
 */
export default function InstrumentChartWidget({ symbol, compact = false, riskLevels }) {
    const { user } = useAuth();
    const tier = normaliseTier(user?.tier);

    const [chartType, setChartType] = useState(loadChartTypePref);
    const [indicators, setIndicators] = useState(() => loadPref(IND_KEY, DEFAULT_INDICATORS));
    const [days, setDays] = useState(compact ? 90 : 180);
    const [bars, setBars] = useState([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);

    useEffect(() => {
        if (!symbol) return;
        let cancelled = false;
        setLoading(true);
        setErr(null);
        (async () => {
            try {
                const { data } = await api.get(`/market/daily/${symbol}`, { params: { days } });
                if (!cancelled) setBars(data.bars || []);
            } catch (e) {
                if (!cancelled) setErr("Could not load chart history for this instrument.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [symbol, days]);

    const onPickType = useCallback((c) => {
        if (!c.unlocked) return;
        setChartType(c.id);
        try { localStorage.setItem(TYPE_KEY, JSON.stringify(c.id)); } catch (e) { /* storage unavailable — non-fatal */ }
    }, []);

    const onToggleIndicator = useCallback((key) => {
        setIndicators((prev) => {
            const next = { ...prev, [key]: { ...prev[key], enabled: !prev[key]?.enabled } };
            try { localStorage.setItem(IND_KEY, JSON.stringify(next)); } catch (e) { /* storage unavailable — non-fatal */ }
            return next;
        });
    }, []);

    const onRenderError = useCallback((msg) => setErr(msg), []);

    return (
        <div data-testid={`instrument-chart-widget-${symbol}`}>
            <ChartControls
                tier={tier}
                chartType={chartType}
                days={days}
                indicators={indicators}
                onPickType={onPickType}
                onPickDays={compact ? undefined : setDays}
                onToggleIndicator={onToggleIndicator}
            />
            <div className="relative border border-border rounded-lg bg-secondary/10">
                <ChartCanvas
                    bars={bars}
                    chartType={chartType}
                    tier={tier}
                    indicators={indicators}
                    riskLevels={riskLevels}
                    height={compact ? 260 : 380}
                    onRenderError={onRenderError}
                />
                {loading && (
                    <div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground bg-card/60" data-testid={`instrument-chart-loading-${symbol}`}>
                        Loading history…
                    </div>
                )}
                {err && !loading && (
                    <div className="absolute inset-0 flex items-center justify-center text-xs text-danger bg-card/60" data-testid={`instrument-chart-error-${symbol}`}>
                        {err}
                    </div>
                )}
            </div>
            <div className="mt-1.5 text-[10px] text-muted-foreground uppercase tracking-widest" data-testid={`instrument-chart-meta-${symbol}`}>
                {bars.length} daily bars · Simulator OHLC store
            </div>
        </div>
    );
}
